import React, { useEffect } from 'react';
import { styled, Button, Flex } from '@goodrx/matisse';
import { useSynapse } from 'app/lib/synaptik';
import { trackEvent } from 'app/lib/analytics';
import { DefaultMessage, MessageProps } from 'app/components/widgets/chat/message-types';

const MessageAction = styled.div`
  padding: ${(p) => p.theme.space[1]}px;
`;

const Container = styled(Flex)`
  flex-direction: column;
  margin-top: 16px;
  width: 100%;
  height: 100%;
  justify-content: space-between;
`;

const ActionButton = styled(Button).attrs(() => ({
  variant: 'primary',
}))`
  border-color: white;
`;

const RESPONSE_COPY_VARIANTS = [
  'Yes, Start My Visit',
  'Yes, Start Consultation',
  'Yes, Start Appointment',
  'Yes, Chat with Clinician',
];

interface StartVisitMessageProps extends MessageProps {
  scrollToBottom: () => void;
}

export const StartVisitMessage: React.FC<StartVisitMessageProps> = ({ message, messageRef, scrollToBottom }) => {
  const [sendMessage, sendingMessage, userId, messagesByDate, hasLaterMessage] = useSynapse(({ chat, user }) => [
    chat.send,
    chat.state.sending,
    user.state.profile?.id,
    chat.state.messagesByDate,
    chat.hasLaterMessage,
  ]);

  useEffect(() => {
    setTimeout(scrollToBottom);
  }, [messagesByDate]);

  // Random enough? Still want this to be deterministic for subsequent renders
  const responseCopy = RESPONSE_COPY_VARIANTS[Number(userId) % RESPONSE_COPY_VARIANTS.length];

  const handleActionClick = async () => {
    sendMessage(responseCopy);
    trackEvent('Start Visit CTA Clicked', { copyText: responseCopy });
  };

  return hasLaterMessage({ message }) ? (
    <DefaultMessage message={message} messageRef={messageRef} fromCurrentUser={false} />
  ) : (
    <Container>
      <DefaultMessage message={message} messageRef={messageRef} fromCurrentUser={false} />
      <MessageAction>
        <ActionButton data-qa="message_action_btn" block onClick={handleActionClick} disabled={sendingMessage}>
          {responseCopy}
        </ActionButton>
      </MessageAction>
    </Container>
  );
};
